import React from 'react';
import styled from '@emotion/styled';
import {
  palette,
  spacing,
  radius,
  shadow,
  layout,
  zIndex,
} from '../../style-utils/theme';
import { down, up } from '../../style-utils/media-queries';

export const InputAndTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${spacing.xs};
`;
export const InputContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  width: 100%;
`;

export const ContentButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  margin: 0 auto;
`;

export const TitleAndSubtitle = styled.div<{ direction: 'row' | 'column' }>`
  display: flex;
  flex-direction: ${({ direction }) => direction};
  flex-wrap: nowrap;
  align-items: ${({ direction }) =>
    direction === 'row' ? 'center' : 'stretch'};
  justify-content: space-between;
  gap: ${spacing.sm};
  min-height: 64px;
  margin-bottom: ${spacing.md};
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 8;
  gap: ${spacing.half};
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-self: center;
  justify-content: flex-end;
  flex: 0 0 auto;
`;

export const LayoutContainer = styled.div<{
  height?: number;
  dashboard?: boolean;
}>`
  display: flex;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${palette.porcelain};
  color: ${palette.ink};

  ${down('md')} {
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
    flex-direction: column;
    /* CLEAR THE FIXED BOTTOM BAR. UNCONDITIONALLY.
     *
     * This used to be gated on the dashboard prop, on the premise that only the
     * dashboard has a fixed bottom bar and reserving the space elsewhere would
     * add dead space to the wizards. The premise was wrong: below md the STAFF
     * rail is also position:fixed at bottom:0 at the same height
     * (sidebar.styled.tsx), so every desk screen had its last ~72px sitting
     * under an opaque, pointer-capturing bar — and on a wizard step that band
     * is exactly where Back / Continue live. A concierge on a phone could fill
     * in all seven steps of a visitor log and then not be able to press Finish.
     *
     * The expression is copied verbatim from iq-dock.styled.tsx, which already
     * clears the same bar, so the two cannot drift. */
    padding-bottom: calc(${layout.navHeight} + var(--safe-bottom));
  }
`;

/**
 * THE MAIN LANDMARK — one element, twenty-seven screens.
 *
 * There was no `<main>` anywhere in the product (2 `styled.main` in 426 files,
 * neither on a desk screen) and no skip link, so a screen-reader user had no
 * way to jump past the rail and the header to the content, on every screen,
 * every time. This wrapper is already the content region on all of them, so
 * making it a `<main>` gives the landmark to every one of them at once instead
 * of asking twenty-seven call sites to remember.
 *
 * `id="main-content"` is the skip link's target (see layout/skip-link.tsx), and
 * tabIndex -1 is what lets a heading-less region actually receive that jump.
 */
const ContentWrapperBase = styled.main<{ dashboard?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: ${({ dashboard }) => (dashboard ? '0' : spacing.lg)};
  overflow: ${({ dashboard }) => (dashboard ? 'hidden' : 'auto')};

  ${down('md')} {
    overflow: visible;
    padding: ${({ dashboard }) => (dashboard ? '0' : spacing.sm)};
  }
`;

/* The id and tabIndex are supplied HERE rather than at twenty-seven call sites,
   because the twenty-eighth would not have them. tabIndex -1 is what makes the
   region able to receive the skip link's jump: without it the browser moves the
   URL fragment and leaves focus where it was. */
export const ContentWrapper: React.FC<
  React.ComponentProps<typeof ContentWrapperBase>
> = ({ id = 'main-content', tabIndex = -1, ...rest }) => (
  <ContentWrapperBase id={id} tabIndex={tabIndex} {...rest} />
);

export const StepAndProgressContainer = styled.div<{ isMainMenu?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  min-height: ${layout.controlHeight};
  flex-shrink: 0;
  ${({ isMainMenu }) =>
    !isMainMenu && `border-bottom: 1px solid ${palette.line};`}
  padding-bottom: ${spacing.sm};
  margin-bottom: ${spacing.sm};
`;

/* A `Step` and a `Progress` USED TO LIVE HERE TOO, and neither was ever built:
   a 40x40 div and a 200x40 div, no content, no styling, handed props a plain
   styled.div drops. Their only consumer was the main menu, which is not a wizard
   and has no steps to indicate, so it was rendering 240px of empty box above its
   own content. Deleted rather than filled in - the real Step and Progress
   components live in components/step and components/progress. */

export const NavbarAndContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  min-width: 0;
  height: 100%;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};

  ${down('md')} {
    flex-direction: column;
    height: auto;
    border-radius: ${radius.sm};
  }

  /* The dashboard reuses this as a full-bleed shell — opt it out of the
     wizard card treatment. */
  &#dashboard-sidebar {
    background: ${palette.porcelain};
    border: none;
    border-radius: 0;
    box-shadow: none;
    animation: none;
  }
`;

export const NavbarContainer = styled.div<{
  dashboard?: boolean;
  isMainMenu?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: ${({ dashboard, isMainMenu }) =>
    dashboard ? '2' : isMainMenu ? '4' : '0 0 260px'};
  max-width: ${({ dashboard, isMainMenu }) =>
    dashboard ? '240px' : isMainMenu ? 'inherit' : '260px'};

  /* THE MAIN MENU'S TITLE COLUMN MUST NOT GROW WITH THE SCREEN.
   *
   * On /main-menu this column holds only the "Main Menu" heading and its one
   * line of subtitle, at flex 4 against the tiles' flex 6. On a phone that
   * reads as a title above its options. On a 1920px display it becomes 40% of
   * the viewport - roughly 700px of empty space between the heading and the
   * first thing anyone wants to press, which is Clock in.
   *
   * Capping the BASIS rather than the flex ratio keeps the small-screen
   * proportions exactly as they were: below lg nothing here applies, and the
   * down(md) rule further down still forces max-width 100% when stacked.
   *
   * NO BACKTICKS IN THIS COMMENT. It sits inside a styled template literal, so
   * a backtick here silently terminates the string and the whole file stops
   * parsing - which is exactly how it broke the first time. */
  ${({ isMainMenu }) =>
    isMainMenu &&
    `${up('lg')} {
       flex: 0 0 320px;
       max-width: 320px;
     }
     ${up('xxl')} {
       flex: 0 0 380px;
       max-width: 380px;
     }`}
  min-width: 0;
  align-items: inherit;
  justify-content: space-between;
  background: ${({ isMainMenu }) =>
    isMainMenu ? 'transparent' : palette.surfaceMuted};
  ${({ isMainMenu }) =>
    !isMainMenu &&
    `border-right: 1px solid ${palette.line};
     border-radius: ${radius.md} 0 0 ${radius.md};`}

  ${down('md')} {
    max-width: 100%;
    ${({ dashboard }) =>
      dashboard &&
      `position: fixed;
       left: 0;
       right: 0;
       bottom: 0;
       width: 100%;
       height: calc(${layout.navHeight} + var(--safe-bottom));
       flex-direction: row;
       align-items: center;
       padding-bottom: var(--safe-bottom);
       overflow: hidden;
       background: ${palette.surfaceGlass};
       /* WP-33 / DS-RESP-02b. surfaceGlass is rgba(255,255,255,0.82) and this
          bar had NO blur behind it, so page content scrolled visibly through
          the 18% that is transparent, directly under the nav labels. That is
          the real source of the "low-contrast or obscured" report - the labels
          were losing contrast against whatever row happened to be passing
          underneath them, not against each other. blur(20px) is what
          design_system.md specifies for a frosted sticky surface and what
          sidebar.styled.tsx:89-91 already applies. NO BACKTICKS IN THIS
          COMMENT - it is inside a styled template literal. */
       -webkit-backdrop-filter: blur(20px);
       backdrop-filter: blur(20px);
       border-top: 1px solid ${palette.line};
       border-right: none;
       border-radius: 0;
       box-shadow: ${shadow.lg};
       z-index: ${zIndex.sticky};`}
    ${({ dashboard, isMainMenu }) =>
      !dashboard &&
      !isMainMenu &&
      `border-right: none;
       border-bottom: 1px solid ${palette.line};
       border-radius: ${radius.sm} ${radius.sm} 0 0;`}
  }
`;

export const ContentAndButtonsContainer = styled.div<{ dashboard?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 6;
  min-width: 0;
  background: transparent;
  padding: ${({ dashboard }) =>
    dashboard ? '0' : `${spacing.xxl} ${spacing.lg} 0`};
  overflow: ${({ dashboard }) => (dashboard ? 'hidden' : 'auto')};

  ${down('md')} {
    overflow: visible;
    padding: ${({ dashboard }) =>
      dashboard ? '0' : `${spacing.md} ${spacing.sm} 0`};
  }
`;

export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.sm};
  padding-bottom: ${spacing.md};
  flex-shrink: 0;

  ${down('sm')} {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: ${spacing.xs};
  }
`;
